import {Mapper, MAPPER_DESCRIPTIONS, MAPPER_NAMES, MAPPER_VALUES, MapperKind} from '@/api/models/Feeds.ts';
import {MemoryFeedField} from '@/pages/feeds/Editor/Mapper/index.tsx';
import Mappers, {ChangeFn} from '@/pages/feeds/Editor/Mapper/Mappers';
import {Button, Classes, Drawer, MenuItem} from '@blueprintjs/core';
import {ItemRenderer, Select} from '@blueprintjs/select';
import {ReactElement, useCallback, useState} from 'react';

export type MappingChangeFn = (field: MemoryFeedField) => void;

interface Props {
	field: MemoryFeedField,
	onClose: () => void,
	onChange: MappingChangeFn,
}

export default function MappingDrawer({field, onClose, onChange}: Props): ReactElement {
	const [mapper, setMapper] = useState<Mapper | null>(field.mapper ?? null);
	const onMapperChange: ChangeFn = useCallback(mapper => {
		setMapper(mapper);

		field.mapper = mapper;
		onChange(field);
	}, [onChange, field]);

	const onMapperKindChange = useCallback((kind: MapperKind) => {
		setMapper(createEmptyMapperFromKind(kind));
	}, []);

	const onRemoveClick = useCallback(() => {
		field.mapper = null;
		onChange(field);
		onClose();
	}, [onChange, onClose, field]);

	return (
		<Drawer title="Field Manipulation" isOpen={true} onClose={onClose}>
			<div className={Classes.DRAWER_BODY} style={{padding: '10px 20px'}}>
				<MapperTypeSelect
					value={mapper?.kind ?? null}
					onSelect={onMapperKindChange}
				/>

				<Mappers mapper={mapper} onChange={onMapperChange} />
			</div>

			<div className={Classes.DRAWER_FOOTER} style={{display: 'flex', justifyContent: 'space-between'}}>
				<Button intent="danger" text="Remove Manipulation" onClick={onRemoveClick} />
				<Button intent="primary" text="Done" onClick={onClose} />
			</div>
		</Drawer>
	);
}

interface MapperTypeSelectProps {
	value: MapperKind | null,
	onSelect: (kind: MapperKind) => void,
}

function MapperTypeSelect({value, onSelect}: MapperTypeSelectProps): ReactElement {
	const renderItem: ItemRenderer<MapperKind> = useCallback((item, {handleClick, handleFocus, modifiers}) => {
		if (!modifiers.matchesPredicate)
			return null;

		return (
			<MenuItem
				key={item}
				text={MAPPER_NAMES[item]}
				label={MAPPER_DESCRIPTIONS[item]}
				active={modifiers.active}
				selected={item === value}
				roleStructure="listoption"
				onClick={handleClick}
				onFocus={handleFocus}
			/>
		);
	}, [value]);

	return (
		<Select
			items={MAPPER_VALUES}
			itemRenderer={renderItem}
			itemPredicate={filterMapperKind}
			onItemSelect={onSelect}
			fill={true}
			popoverProps={{matchTargetWidth: true}}
		>
			<Button
				text={value ? MAPPER_NAMES[value] : 'Select a type...'}
				fill={true}
				endIcon="chevron-down"
				alignText="start"
			/>
		</Select>
	);
}

function filterMapperKind(query: string, item: MapperKind): boolean {
	query = query.toLowerCase();
	return MAPPER_NAMES[item].toLowerCase().includes(query) || MAPPER_DESCRIPTIONS[item].toLowerCase().includes(query);
}

export function createEmptyMapperFromKind(kind: MapperKind): Mapper {
	switch (kind) {
		case MapperKind.Chain:
			return {
				kind,
				mappers: [],
			};

		case MapperKind.Lookup:
			return {
				kind,
				valueMap: {},
			};

		case MapperKind.Split:
			return {
				kind,
				separator: '|',
			};

		case MapperKind.TakeOne:
			return {
				kind,
				index: 0,
			};

		default:
			throw new Error(`Unrecognized mapper kind "${kind}"`);
	}
}
